import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getFornecedores, uploadFile } from '../../services/api';
import { ContratoInput, ContratoItem } from '../../services/types';

interface ContratoModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (contrato: ContratoInput) => Promise<void>;
    obraId: number;
    contratoToEdit?: ContratoItem | null;
}

export const ContratoModal: React.FC<ContratoModalProps> = ({ open, onClose, onSave, obraId, contratoToEdit }) => {
    const [descricao, setDescricao] = useState('');
    const [fornecedorId, setFornecedorId] = useState<string>('');
    const [valorTotal, setValorTotal] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [status, setStatus] = useState('Ativo');
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data: fornecedores = [], isLoading: loadingFornecedores } = useQuery({
        queryKey: ['fornecedores'],
        queryFn: getFornecedores
    });

    useEffect(() => {
        if (contratoToEdit) {
            setDescricao(contratoToEdit.descricao);
            setFornecedorId(String(contratoToEdit.fornecedor_id));
            setValorTotal(String(contratoToEdit.valor_total));
            setDataInicio(contratoToEdit.data_inicio || '');
            setDataFim(contratoToEdit.data_fim || '');
            setStatus(contratoToEdit.status);
            setFile(null);
        } else {
            setDescricao('');
            setFornecedorId('');
            setValorTotal('');
            setDataInicio('');
            setDataFim('');
            setStatus('Ativo');
            setFile(null);
        }
        setError(null);
    }, [contratoToEdit, open]);

    const handleSubmit = async () => {
        if (!descricao || !fornecedorId || !valorTotal) {
            setError('Preencha os campos obrigatórios.');
            return;
        }

        try {
            setUploading(true);
            let arquivoUrl = contratoToEdit?.arquivo_url || null;

            if (file) {
                const sanitizedFileName = file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9.-]/g, '_');
                const fileName = `contratos/${obraId}/${Date.now()}_${sanitizedFileName}`;
                const uploadResult = await uploadFile('documentos', fileName, file);
                if (uploadResult) {
                    arquivoUrl = fileName;
                }
            }

            await onSave({
                id: contratoToEdit?.id,
                obra_id: obraId,
                fornecedor_id: Number(fornecedorId),
                descricao,
                valor_total: Number(valorTotal),
                data_inicio: dataInicio || null,
                data_fim: dataFim || null,
                status,
                arquivo_url: arquivoUrl
            });
            onClose();
        } catch (err: any) {
            setError(err.message || 'Erro ao salvar contrato.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{contratoToEdit ? 'Editar Contrato' : 'Novo Contrato'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        select
                        label="Fornecedor"
                        value={fornecedorId}
                        onChange={(e) => setFornecedorId(e.target.value)}
                        fullWidth
                        required
                    >
                        {loadingFornecedores ? (
                            <MenuItem disabled><CircularProgress size={20} /></MenuItem>
                        ) : (
                            fornecedores.map((f: any) => (
                                <MenuItem key={f.id} value={f.id}>{f.nome_razao_social}</MenuItem>
                            ))
                        )}
                    </TextField>
                    <TextField
                        label="Descrição"
                        value={descricao}
                        onChange={(e) => setDescricao(e.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label="Valor Total (R$)"
                        type="number"
                        value={valorTotal}
                        onChange={(e) => setValorTotal(e.target.value)}
                        fullWidth
                        required
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Data Início"
                            type="date"
                            value={dataInicio}
                            onChange={(e) => setDataInicio(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: "9999-12-31" }}
                        />
                        <TextField
                            label="Data Fim"
                            type="date"
                            value={dataFim}
                            onChange={(e) => setDataFim(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: "9999-12-31" }}
                        />
                    </Box>
                    <TextField
                        select
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        fullWidth
                    >
                        <MenuItem value="Ativo">Ativo</MenuItem>
                        <MenuItem value="Finalizado">Finalizado</MenuItem>
                        <MenuItem value="Cancelado">Cancelado</MenuItem>
                    </TextField>
                    <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                    >
                        {file ? file.name : (contratoToEdit?.arquivo_url ? 'Substituir Arquivo' : 'Anexar Contrato (PDF)')}
                        <input
                            type="file"
                            hidden
                            accept=".pdf,image/*"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancelar</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
                    {uploading ? <CircularProgress size={24} /> : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
